import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../components/AppProvider';
import { Lock, Check, AlertCircle } from 'lucide-react';
import { initRecaptcha, executeRecaptcha, verifyRecaptcha, unloadRecaptcha, isDevEnvironment } from '../../lib/recaptcha';

// Componente interno con reCAPTCHA
function ResetPasswordForm() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [hash, setHash] = useState<string | null>(null);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const navigate = useNavigate();
  const { toast } = useApp();
  
  // Ottieni la chiave del sito reCAPTCHA dalle variabili d'ambiente
  const recaptchaSiteKey = import.meta.env.VITE_GOOGLE_RECAPTCHA_SITE_KEY || 
                          '6LdR1x0rAAAAAEaN_aDN9JSeAg7tYFssz_S_6ag7'; // Fallback alla chiave fornita
  
  // Inizializza reCAPTCHA
  useEffect(() => {
    const loadRecaptcha = async () => {
      try {
        await initRecaptcha(recaptchaSiteKey);
        setRecaptchaReady(true);
      } catch (error) {
        console.error('Failed to initialize reCAPTCHA:', error);
        // In development, we'll still consider it ready for testing
        if (isDevEnvironment()) {
          setRecaptchaReady(true);
        }
      }
    };
    
    loadRecaptcha();
  }, [recaptchaSiteKey]);

  useEffect(() => {
    // Extract hash from URL
    const hashFragment = window.location.hash;
    const params = new URLSearchParams(hashFragment.substring(1));
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    if (accessToken && refreshToken) {
      // Il client non legge la sessione dall'URL (detectSessionInUrl: false): va aperta qui
      setHash(hashFragment.substring(1));
      window.history.replaceState(null, '', window.location.pathname);
      supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken }).then(({ error }) => {
        if (error) setError('Link di recupero non valido. Richiedi un nuovo link di recupero password.');
      });
    } else {
      setError('Link di recupero non valido. Richiedi un nuovo link di recupero password.');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError('La password deve contenere almeno 6 caratteri');
      return;
    }

    if (password !== confirmPassword) {
      setError('Le password non corrispondono');
      return;
    }

    setLoading(true);
    try {
      // Bypass reCAPTCHA in ambiente di sviluppo
      if (isDevEnvironment() || !recaptchaReady) {
        console.log('Ambiente di sviluppo o reCAPTCHA non pronto. Bypassing reCAPTCHA...');
        const { error } = await supabase.auth.updateUser({ password });
        
        if (error) throw error;
        
        // Rimuovi reCAPTCHA dalla pagina dopo l'aggiornamento della password (anche in dev per sicurezza)
        if (recaptchaReady) {
          console.log('[ResetPassword] Rimozione reCAPTCHA dopo aggiornamento password (dev)');
          unloadRecaptcha();
        }
        
        setSuccess(true);
        toast.success('Password aggiornata con successo');
        
        // Redirect to login after a delay
        setTimeout(() => {
          navigate('/login');
        }, 3000);
        return;
      }
      
      // Esegui reCAPTCHA per la reimpostazione della password
      const recaptchaToken = await executeRecaptcha('reset_password_confirm');
      
      if (!recaptchaToken) {
        throw new Error('Errore nell\'esecuzione di reCAPTCHA. Riprova più tardi.');
      }
      
      // Verifica il token reCAPTCHA
      const recaptchaResult = await verifyRecaptcha(recaptchaToken, 'reset_password_confirm');
      
      if (!recaptchaResult.success) {
        throw new Error(recaptchaResult.message || 'Verifica di sicurezza fallita. Riprova.');
      }
      
      // Se reCAPTCHA è verificato con successo, procedi con l'aggiornamento della password
      const { error } = await supabase.auth.updateUser({ password });
      
      if (error) throw error;
      
      // Rimuovi reCAPTCHA dalla pagina dopo l'aggiornamento della password
      if (recaptchaReady && !isDevEnvironment()) {
        console.log('[ResetPassword] Rimozione reCAPTCHA dopo aggiornamento password');
        unloadRecaptcha();
      }
      
      setSuccess(true);
      toast.success('Password aggiornata con successo');
      
      // Redirect to login after a delay
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error: any) {
      console.error('Reset password error:', error);
      
      // Determina se è un errore di reCAPTCHA o di autenticazione
      if (error.message?.includes('reCAPTCHA') || error.message?.includes('sicurezza')) {
        setError(`Errore di verifica: ${error.message}`);
      } else {
        setError(error.message || 'Impossibile aggiornare la password');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="h-16 w-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
            <Lock className="h-8 w-8 text-white" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-black">
          {success ? 'Password aggiornata' : 'Reimposta la tua password'}
        </h2>
        <p className="mt-2 text-center text-sm text-black/60">
          {success 
            ? 'La tua password è stata aggiornata con successo. Verrai reindirizzato alla pagina di login.' 
            : 'Inserisci la tua nuova password per accedere al tuo account'}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="glass-card sm:px-10">
          {error && (
            <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
              <span className="block sm:inline">{error}</span>
            </div>
          )}
          
          {success ? (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <p className="mt-4 text-black/80">
                Verrai reindirizzato alla pagina di login tra pochi secondi...
              </p>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-black">
                  Nuova Password
                </label>
                <div className="mt-1 relative rounded-lg shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-black/40" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2.5 glass-card text-black rounded-lg leading-5 placeholder-black/40 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent sm:text-sm"
                    placeholder="••••••••"
                  />
                </div>
                <p className="mt-1 text-sm text-black/60">
                  La password deve contenere almeno 6 caratteri
                </p>
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-black">
                  Conferma Password
                </label>
                <div className="mt-1 relative rounded-lg shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-black/40" />
                  </div>
                  <input
                    id="confirm-password"
                    name="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2.5 glass-card text-black rounded-lg leading-5 placeholder-black/40 focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent sm:text-sm"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading || (!isDevEnvironment() && !recaptchaReady)}
                  className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-lg text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all duration-200"
                >
                  {loading ? 'Aggiornamento in corso...' : 'Aggiorna Password'}
                </button>
              </div>
            </form>
          )}
          
          {/* Nota informativa reCAPTCHA */}
          {!success && (
            <div className="mt-4 text-xs text-center text-black/50 flex items-center justify-center">
              <AlertCircle className="h-3 w-3 mr-1 flex-shrink-0" />
              <span>Questo sito è protetto da reCAPTCHA per garantire la sicurezza dell'account</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Componente wrapper per ResetPassword
export function ResetPassword() {
  return <ResetPasswordForm />;
}